import { type ReactNode } from "react";
import { cn } from "@/lib/cn";

export interface PageHeaderProps {
  /** Page H1 — big & bold per DESIGN-SYSTEM §9.5 (28–30px / 700 / tracking-tight). */
  title: ReactNode;
  /** Optional one-line description under the title. */
  subtitle?: ReactNode;
  /** Breadcrumbs slot, rendered above the title. */
  breadcrumbs?: ReactNode;
  /** Primary action(s) slot, rendered top-right (buttons / menus). */
  actions?: ReactNode;
  /** Tabs slot, rendered below the title block. */
  tabs?: ReactNode;
  className?: string;
}

/**
 * PageHeader — standardized page top (DESIGN-SYSTEM §5, §9.5).
 * Layout: [breadcrumbs] → [ title + subtitle | actions ] → [tabs].
 */
export function PageHeader({
  title,
  subtitle,
  breadcrumbs,
  actions,
  tabs,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn("flex flex-col gap-4", className)}>
      {breadcrumbs}
      <div className="flex flex-wrap items-start justify-between gap-4">
        {/* The title block is the one that gives way: it shrinks (and its
            subtitle wraps) to keep the actions beside it, down to 16rem.
            Below that — a phone — the actions drop to their own line. */}
        <div className="min-w-[min(100%,16rem)] flex-1 space-y-1">
          <h1 className="text-[28px] font-bold leading-tight tracking-tight text-fg-primary">
            {title}
          </h1>
          {/* Cap the measure: some subtitles are two-sentence explanations and
              ran the full 1232px of the content column. */}
          {subtitle && (
            <p className="max-w-3xl text-sm leading-snug text-fg-muted">{subtitle}</p>
          )}
        </div>
        {/* Wraps inside itself too: three buttons side by side are wider than
            a phone, and a row that can't wrap pushes the last one off-screen. */}
        {actions && (
          <div className="flex max-w-full flex-wrap items-center gap-2">{actions}</div>
        )}
      </div>
      {tabs}
    </header>
  );
}
PageHeader.displayName = "PageHeader";
